import React, { useState, useEffect } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import WidgetWrapper from './WidgetWrapper';
import { entries } from '../../services/api';

const RevenueWidget = ({ darkMode = false, onError }) => {
  const [windowWidth, setWindowWidth] = useState(() => typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = windowWidth < 800;
  const chartHeight = isMobile ? 200 : 300;

  const [revenueData, setRevenueData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const now = new Date();
        const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 5, 1);
        const startDate = sixMonthsAgo.toISOString().split('T')[0];
        const endDate = now.toISOString().split('T')[0];
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

        // Fetch all entries for the trailing 6 months
        const res = await entries.list({ startDate, endDate, limit: 1000 });
        const allEntries = res.data?.entries || res.data || [];

        // Group by month and sum totalCost
        const monthMap = {};
        for (let i = 5; i >= 0; i--) {
          const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
          const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
          monthMap[key] = { month: monthNames[d.getMonth()], revenue: 0 };
        }

        allEntries.forEach(e => {
          if (!e.date) return;
          const key = e.date.substring(0, 7); // YYYY-MM
          if (monthMap[key]) {
            monthMap[key].revenue += (e.totalCost || 0);
          }
        });

        const finalResults = Object.values(monthMap);
        setRevenueData(finalResults);
      } catch (error) {
        console.error('RevenueWidget error:', error);
        onError?.('RevenueWidget', error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [onError]);

  const subTextColor = darkMode ? '#9ca3af' : '#6b7280';
  const borderColor = darkMode ? '#374151' : '#e5e7eb';

  return (
    <WidgetWrapper title="💰 Revenue (Trailing 6 Months)" loading={loading} darkMode={darkMode}>
      <ResponsiveContainer width="100%" height={chartHeight}>
        <AreaChart data={revenueData}>
          <defs>
            <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#2563eb" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={borderColor} />
          <XAxis dataKey="month" fontSize={12} stroke={subTextColor} />
          <YAxis tickFormatter={(value) => `$${value / 1000}k`} fontSize={12} stroke={subTextColor} />
          <Tooltip
            formatter={(value) => [`$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 'Revenue']}
            contentStyle={{
              background: darkMode ? '#1f2937' : '#fff',
              border: `1px solid ${borderColor}`
            }}
          />
          <Area type="monotone" dataKey="revenue" stroke="#2563eb" fill="url(#colorRevenue)" />
        </AreaChart>
      </ResponsiveContainer>
    </WidgetWrapper>
  );
};

RevenueWidget.widgetId = 'revenue';
RevenueWidget.title = '💰 Revenue (Trailing 6 Months)';
RevenueWidget.defaultSize = { cols: 6 };
RevenueWidget.category = 'business';

export default RevenueWidget;